import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Calendar, Ruler, Weight, Activity, Info, Zap, Target, Baby, AlertCircle, TestTube2 } from 'lucide-react';
import { Client } from '@/types/client';
import { differenceInYears, differenceInMonths } from 'date-fns';
import { formatWeight, formatHeight, kgToLbs, lbsToKg } from '@/utils/unit-conversion';
import { calculateWHR, calculateIBWMetric, calculateIBWImperial, cmToInches, convertInchesToCm } from '@/utils/anthropometric-calculations';
import { getViableFormulas, getCalculationFormulas } from '@/lib/api/clients';
import { CalculationFormula } from '@/types/calculation-formulas';
import { resolveAllowedActivityLevels, ResolvedActivityLevels } from '@/utils/activity-levels';
import {
  getFormulaFallbackDisplayName,
  getPreferredFormulaFallback,
} from '@/clients/onboarding/components/formula-utils';
import {
  WEIGHT_LOSS_GOAL_OPTIONS,
  GENERAL_WEIGHT_GOAL_OPTIONS,
  PREGNANCY_TRIMESTER_OPTIONS,
  LACTATION_STATUS_OPTIONS,
  GENDER_OPTIONS,
  FEEDING_TYPE_OPTIONS
} from '@/clients/onboarding/components/constants';
import FormulaValidationModal from '@/components/clients/shared/FormulaValidationModal';

const normalizeGender = (value: unknown): 'female' | 'male' | '' => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['female', 'f', 'woman', 'women'].includes(normalized)) return 'female';
  if (['male', 'm', 'man', 'men'].includes(normalized)) return 'male';
  return '';
};

const formatGenderDisplayValue = (value: unknown): string => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'prefer_not_to_say') return 'Prefer not to say';
  if (normalized === 'non_binary') return 'Non-binary';
  if (normalized === 'male') return 'Male';
  if (normalized === 'female') return 'Female';
  return String(value).replace(/_/g, ' ');
};

const SEX_AT_BIRTH_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
] as const;

const normalizePregnancyTrimester = (value: unknown): string => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (
    normalized === 'first' ||
    normalized === '1' ||
    normalized === '1st' ||
    normalized === 'first_trimester' ||
    normalized === '1st trimester' ||
    normalized === 'first trimester'
  ) return 'first';
  if (
    normalized === 'second' ||
    normalized === '2' ||
    normalized === '2nd' ||
    normalized === 'second_trimester' ||
    normalized === '2nd trimester' ||
    normalized === 'second trimester'
  ) return 'second';
  if (
    normalized === 'third' ||
    normalized === '3' ||
    normalized === '3rd' ||
    normalized === 'third_trimester' ||
    normalized === '3rd trimester' ||
    normalized === 'third trimester'
  ) return 'third';
  if (normalized === 'pregnant') return 'first';
  if (['none', 'not pregnant', 'not_pregnant', 'non-pregnant'].includes(normalized)) return 'none';
  return 'none';
};

const normalizeLactationStatus = (value: unknown): string => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['0-6', '0_6', '0to6', '0 to 6'].includes(normalized)) return '0-6';
  if (['6-12', '6_12', '6to12', '6 to 12'].includes(normalized)) return '6-12';
  if (['12-18', '12_18', '12to18', '12 to 18'].includes(normalized)) return '12-18';
  if (['18-24', '18_24', '18to24', '18 to 24'].includes(normalized)) return '18-24';
  if (['yes', 'true', 'lactating', 'breastfeeding', 'breast_feeding'].includes(normalized)) return '0-6';
  return 'none';
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// Helper to flatten symptoms
const getSymptomsDisplay = (symptoms: any) => {
  if (!symptoms) return 'None reported';

  if (Array.isArray(symptoms)) {
    if (symptoms.length === 0) return 'None reported';
    // Array of strings or objects
    const names = symptoms.map(s => {
      if (typeof s === 'string') return s;
      if (typeof s === 'object' && s?.symptomName) return s.symptomName;
      return null;
    }).filter(Boolean);
    return names.length > 0 ? Array.from(new Set(names)).join(', ') : 'None reported';
  }

  if (typeof symptoms === 'object') {
    const all = new Set<string>();
    Object.values(symptoms).forEach((val: any) => {
      if (Array.isArray(val)) {
        val.forEach(v => {
          if (typeof v === 'string') all.add(v);
          else if (v?.symptomName) all.add(v.symptomName);
        });
      } else if (typeof val === 'string') {
        all.add(val);
      }
    });
    return all.size > 0 ? Array.from(all).join(', ') : 'None reported';
  }

  return 'None reported';
};

interface ClientProfileSummaryProps {
  client: Client;
  isEditMode?: boolean;
  editData?: {
    gender?: string;
    sexAtBirth?: string;
    heightCm?: number;
    weightKg?: number;
    [key: string]: any;
  };
  onDataChange?: (field: string, value: any) => void;
}

const ClientProfileSummary: React.FC<ClientProfileSummaryProps> = ({
  client,
  isEditMode = false,
  editData = {},
  onDataChange
}) => {
  // Use client's preferred measurement system, fallback to metric if not set
  const measurementSystem = (client.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';

  // Local state for imperial height inputs
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [displayWeightValue, setDisplayWeightValue] = useState('');
  const [allFormulas, setAllFormulas] = useState<CalculationFormula[]>([]);
  const [viableFormulas, setViableFormulas] = useState<CalculationFormula[]>([]);
  const [isLoadingFormulas, setIsLoadingFormulas] = useState(false);
  const [allowedActivityLevels, setAllowedActivityLevels] = useState<ResolvedActivityLevels | null>(null);

  // Local state for imperial waist/hip inputs
  const [waistInches, setWaistInches] = useState('');
  const [hipInches, setHipInches] = useState('');

  // Formula validation modal state
  const [showFormulaValidationModal, setShowFormulaValidationModal] = useState(false);
  const [nonViableFormulaName, setNonViableFormulaName] = useState('');

  // Use only values from client prop (from API)
  // Calculate age from editData.dateOfBirth if in edit mode, otherwise use client.dateOfBirth
  const dateOfBirthForAge = isEditMode && editData.dateOfBirth
    ? editData.dateOfBirth
    : client.dateOfBirth;
  const displayAge = dateOfBirthForAge ? differenceInYears(new Date(), new Date(dateOfBirthForAge)) : undefined;
  // Calculate age in months to determine if client is an infant (0-12 months)
  const ageInMonths = dateOfBirthForAge ? differenceInMonths(new Date(), new Date(dateOfBirthForAge)) : null;
  const isInfant = ageInMonths !== null && ageInMonths >= 0 && ageInMonths < 12;
  const rawGenderValue = isEditMode
    ? (editData.gender !== undefined ? editData.gender : (client.gender || ''))
    : (client.gender || '');
  const clientSexAtBirthValue = client.sexAtBirth ?? null;
  const rawSexAtBirthValue = isEditMode
    ? (editData.sexAtBirth !== undefined ? editData.sexAtBirth : (clientSexAtBirthValue ?? ''))
    : (clientSexAtBirthValue ?? '');
  const showSexAtBirthAsNull = !isEditMode && clientSexAtBirthValue === null;

  /** Show sex at birth only when gender is something other than male/female (e.g. non-binary, other). */
  const showSexAtBirthField =
    rawGenderValue !== 'male' && rawGenderValue !== 'female';

  const normalizedGenderForCalculations = normalizeGender(rawGenderValue);
  const normalizedSexAtBirthForCalculations = normalizeGender(rawSexAtBirthValue);

  const displayGender = formatGenderDisplayValue(
    isEditMode
      ? (editData.gender !== undefined ? editData.gender : client.gender)
      : client.gender
  );
  const displaySexAtBirth = formatGenderDisplayValue(
    isEditMode
      ? (editData.sexAtBirth !== undefined ? editData.sexAtBirth : clientSexAtBirthValue)
      : clientSexAtBirthValue
  );
  const biologicalSexForCalculations = normalizedGenderForCalculations || normalizedSexAtBirthForCalculations;
  const isFemaleUnder18 = biologicalSexForCalculations === 'female' && displayAge !== undefined && displayAge < 18;
  const displayHeightCm = isEditMode ? (editData.heightCm || client.heightCm) : client.heightCm;
  const displayWeightKg = isEditMode ? (editData.weightKg || client.weightKg) : client.weightKg;
  const displayBMI = client.bmi;
  const displayBMICategory = client.bmiCategory;
  const displayWaistCm = isEditMode ? (editData.waistCircumferenceCm !== undefined ? editData.waistCircumferenceCm : client.waistCircumferenceCm) : client.waistCircumferenceCm;
  const displayHipCm = isEditMode ? (editData.hipCircumferenceCm !== undefined ? editData.hipCircumferenceCm : client.hipCircumferenceCm) : client.hipCircumferenceCm;
  const displayBodyFat = isEditMode ? (editData.bodyFatPercentage !== undefined ? editData.bodyFatPercentage : client.bodyFatPercentage) : client.bodyFatPercentage;
  const displayFormulaId = isEditMode
    ? (editData.formulaId !== undefined ? editData.formulaId : client.bmrFormulaId)
    : client.bmrFormulaId;
  const displayWeightGoal = isEditMode
    ? (editData.weightLossGoal !== undefined ? editData.weightLossGoal : (client.lactationGoal || client.weightGoal))
    : (client.lactationGoal || client.weightGoal);
  const normalizedPregnancyTrimester = normalizePregnancyTrimester(
    client.pregnancyTrimester
  );
  const displayPregnancyTrimester = isEditMode
    ? (
      editData.pregnancyTrimester !== undefined
        ? normalizePregnancyTrimester(editData.pregnancyTrimester)
        : normalizedPregnancyTrimester
    )
    : normalizedPregnancyTrimester;
  const displayIsTwinPregnancy = isEditMode ? (editData.isTwinPregnancy !== undefined ? editData.isTwinPregnancy : client.isTwinPregnancy) : client.isTwinPregnancy;
  const normalizedLactationStatus = normalizeLactationStatus(
    client.lactationPeriod
  );
  const displayLactationStatus = isEditMode
    ? (
      editData.lactationPeriod !== undefined
        ? normalizeLactationStatus(editData.lactationPeriod)
        : normalizedLactationStatus
    )
    : normalizedLactationStatus;
  const isLactating = displayLactationStatus && displayLactationStatus !== 'none';

  // Pregnancy check
  const isPregnant = displayPregnancyTrimester !== 'none';
  const displayBmr = isEditMode
    ? (
      editData.bmr !== undefined
        ? toNullableNumber(editData.bmr)
        : toNullableNumber(client.bmr)
    )
    : toNullableNumber(client.bmr);
  const displayEer = isEditMode ? (editData.eer || client.eer) : client.eer;
  const displayActivityLevel = isEditMode ? (editData.activityLevel !== undefined ? editData.activityLevel : client.activityLevel) : client.activityLevel;
  const displayFeedingType = isEditMode ? (editData.feedingType !== undefined ? editData.feedingType : client.feedingType) : client.feedingType;

  // Find formula name for display (when not in edit mode)
  // Convert both to string for comparison to handle number/string mismatches
  const formulaName = client.calculationDetails.formulaDisplayName;
  const formulaId = client.calculationDetails.formulaUsed; // Check the actual formula ID used

  const formulaSelectValue = displayFormulaId && allFormulas.length > 0
    ? (allFormulas.find(f => String(f.id) === String(displayFormulaId)) ? String(displayFormulaId) : '')
    : '';

  // Determine if BMR should be hidden (when IOM formula is used OR when client is pregnant/lactating)
  const isIomFormula = formulaId === 'iom' || (displayFormulaId && String(displayFormulaId) === 'iom');
  // Hide BMR for pregnant/lactating clients (grayed out = not shown) or if IOM formula is used
  // For other clients, only show if backend provides it
  const shouldShowBmr = !isIomFormula && !isPregnant && !isLactating && displayBmr !== undefined && displayBmr !== null;

  // Calculate WHR
  const whr = displayWaistCm && displayHipCm ? calculateWHR(displayWaistCm, displayHipCm) : null;

  // Calculate IBW
  const isMale = biologicalSexForCalculations === 'male';
  const ibw = displayHeightCm
    ? (measurementSystem === 'imperial'
      ? calculateIBWImperial(displayHeightCm / 2.54, isMale)
      : calculateIBWMetric(displayHeightCm, isMale))
    : null;

  // Format height and weight based on measurement system
  const formattedHeight = displayHeightCm ? formatHeight(displayHeightCm, measurementSystem) : null;
  const formattedWeight = displayWeightKg ? formatWeight(displayWeightKg, measurementSystem) : null;

  // Fetch formulas and activity levels based on client's age and gender
  useEffect(() => {
    const fetchFormulas = async () => {
      setIsLoadingFormulas(true);
      try {
        // Use viable formulas API if we have age and gender
        if (displayAge !== undefined && biologicalSexForCalculations) {
          const response = await getViableFormulas({
            age: displayAge,
            gender: biologicalSexForCalculations,
          });
          setAllFormulas(response.allFormulas || []);
          setViableFormulas(response.viableFormulas || []);
          setAllowedActivityLevels(
            resolveAllowedActivityLevels({
              age: displayAge,
              biologicalSex: biologicalSexForCalculations === 'male' || biologicalSexForCalculations === 'female'
                ? biologicalSexForCalculations
                : '',
              backendAllowedActivityLevels: response.allowedActivityLevels || null,
            })
          );
        } else {
          // Fallback to getting all formulas
          const response = await getCalculationFormulas();
          if (response && response.formulas) {
            const formulasArray = Array.isArray(response.formulas) ? response.formulas : [];
            setAllFormulas(formulasArray);
            setViableFormulas(formulasArray); // Treat all as viable in fallback
            setAllowedActivityLevels(
              resolveAllowedActivityLevels({
                age: displayAge,
                biologicalSex: biologicalSexForCalculations === 'male' || biologicalSexForCalculations === 'female'
                  ? biologicalSexForCalculations
                  : '',
                backendAllowedActivityLevels: null,
              })
            );
          }
        }
      } catch (error) {
        console.error('Error fetching formulas:', error);
        // Fallback to getting all formulas
        try {
          const response = await getCalculationFormulas();
          if (response && response.formulas) {
            const formulasArray = Array.isArray(response.formulas) ? response.formulas : [];
            setAllFormulas(formulasArray);
            setViableFormulas(formulasArray);
            setAllowedActivityLevels(
              resolveAllowedActivityLevels({
                age: displayAge,
                biologicalSex: biologicalSexForCalculations === 'male' || biologicalSexForCalculations === 'female'
                  ? biologicalSexForCalculations
                  : '',
                backendAllowedActivityLevels: null,
              })
            );
          }
        } catch (fallbackError) {
          console.error('Error fetching fallback formulas:', fallbackError);
        }
      } finally {
        setIsLoadingFormulas(false);
      }
    };
    fetchFormulas();
  }, [displayAge, biologicalSexForCalculations, isEditMode, editData?.dateOfBirth]);

  // Validate and reset activity level if it's not in the allowed options after age/gender change
  // Skip for infants (0-12 months) as they don't need activity level
  useEffect(() => {
    if (isEditMode && displayActivityLevel && allowedActivityLevels && !isInfant) {
      const isValidActivityLevel = allowedActivityLevels.options.some(
        option => option.value === displayActivityLevel
      );
      if (!isValidActivityLevel) {
        // Find the default activity level or use the first one
        const defaultOption = allowedActivityLevels.options.find(opt => opt.isDefault)
          || allowedActivityLevels.options[0];
        if (defaultOption && onDataChange) {
          onDataChange('activityLevel', defaultOption.value);
        }
      }
    }
  }, [allowedActivityLevels, isEditMode, displayActivityLevel, isInfant, onDataChange]);

  // Clear pregnancy and lactation values when dateOfBirth or gender changes and person becomes female under 18
  useEffect(() => {
    if (isEditMode && isFemaleUnder18 && onDataChange) {
      // Clear pregnancy-related fields
      if (displayPregnancyTrimester && displayPregnancyTrimester !== 'none') {
        onDataChange('pregnancyTrimester', 'none');
      }
      if (displayIsTwinPregnancy) {
        onDataChange('isTwinPregnancy', false);
      }
      // Clear lactation-related fields
      if (displayLactationStatus && displayLactationStatus !== 'none') {
        onDataChange('lactationPeriod', 'none');
      }
      // Clear weight loss goal if it was set for lactation
      if (displayWeightGoal && (client.lactationGoal || client.weightGoal)) {
        onDataChange('weightLossGoal', null);
      }
    }
  }, [isEditMode, isFemaleUnder18, displayPregnancyTrimester, displayIsTwinPregnancy, displayLactationStatus, displayWeightGoal, onDataChange, client.lactationGoal, client.weightGoal]);

  // Initialize formulaId in editData when entering edit mode and formulas are loaded
  useEffect(() => {
    if (isEditMode && client.bmrFormulaId && allFormulas.length > 0 && editData.formulaId === undefined) {
      // Find the formula that matches the client's formulaId
      const matchingFormula = allFormulas.find(f => String(f.id) === String(client.bmrFormulaId));
      if (matchingFormula) {
        // Set the formulaId in editData to ensure it's pre-filled
        // Convert to string to match API type (formula IDs are strings)
        onDataChange?.('formulaId', String(client.bmrFormulaId));
      }
    }
  }, [isEditMode, client.bmrFormulaId, allFormulas.length, editData.formulaId, onDataChange]);

  /**
   * Handler for formula selection with validation
   * Checks if the selected formula is viable for the client's age
   * If not viable, reverts to the recommended formula and shows a warning
   */
  const handleFormulaChange = useCallback((value: string) => {
    // Check if the selected formula is viable
    // If viableFormulas is empty, treat all as viable (fallback behavior)
    const hasViableFormulas = viableFormulas && viableFormulas.length > 0;
    const isViable = !hasViableFormulas || viableFormulas.some(f => String(f.id) === value);

    if (!isViable) {
      // Get the formula display name for the warning modal
      const selectedFormula = allFormulas.find(f => String(f.id) === value);
      const formulaNameStr = selectedFormula?.displayName || selectedFormula?.formulaName || value;
      setNonViableFormulaName(formulaNameStr);
      setShowFormulaValidationModal(true);

      const defaultFormula = getPreferredFormulaFallback(viableFormulas, allFormulas);
      if (defaultFormula) {
        onDataChange?.('formulaId', defaultFormula.id);
      }
    } else {
      // Formula is viable, update normally - keep as string to match API type
      onDataChange?.('formulaId', value || undefined);
    }
  }, [allFormulas, viableFormulas, onDataChange]);

  /**
   * Handler for formula validation modal confirmation
   */
  const handleFormulaValidationConfirm = useCallback(() => {
    setShowFormulaValidationModal(false);
    setNonViableFormulaName('');
  }, []);

  // Get the default formula display name for the modal (for informational purposes)
  const defaultFormulaDisplayName = useMemo(() => {
    const fallbackFormula = getPreferredFormulaFallback(viableFormulas, allFormulas);
    if (fallbackFormula) return getFormulaFallbackDisplayName(fallbackFormula);

    return 'a recommended formula';
  }, [allFormulas, viableFormulas]);

  // Track if we've initialized imperial values to prevent re-initialization when user types
  const [hasInitializedImperial, setHasInitializedImperial] = useState(false);

  // Initialize imperial values when entering edit mode (only once)
  useEffect(() => {
    if (isEditMode && measurementSystem === 'imperial' && !hasInitializedImperial) {
      // Convert height to feet/inches for imperial
      if (displayHeightCm) {
        const totalInches = displayHeightCm / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);
        setHeightFeet(feet.toString());
        setHeightInches(inches.toString());
      }
      // Convert weight to lbs for imperial
      if (displayWeightKg !== undefined && displayWeightKg !== null) {
        setDisplayWeightValue(kgToLbs(Number(displayWeightKg)).toFixed(1));
      }
      // Convert waist to inches (only if value exists)
      if (displayWaistCm !== undefined && displayWaistCm !== null) {
        const inches = cmToInches(Number(displayWaistCm));
        setWaistInches(inches % 1 === 0 ? inches.toFixed(0) : inches.toFixed(1));
      } else {
        setWaistInches('');
      }
      // Convert hip to inches (only if value exists)
      if (displayHipCm !== undefined && displayHipCm !== null) {
        const inches = cmToInches(Number(displayHipCm));
        setHipInches(inches % 1 === 0 ? inches.toFixed(0) : inches.toFixed(1));
      } else {
        setHipInches('');
      }
      setHasInitializedImperial(true);
    } else if (isEditMode && measurementSystem === 'metric' && !hasInitializedImperial) {
      // For metric, use values directly
      if (displayWeightKg) {
        setDisplayWeightValue(displayWeightKg.toString());
      }
      setWaistInches('');
      setHipInches('');
      setHasInitializedImperial(true);
    } else if (!isEditMode) {
      // Reset when exiting edit mode
      setHasInitializedImperial(false);
      setWaistInches('');
      setHipInches('');
    }
  }, [isEditMode, measurementSystem]); // Only depend on edit mode and measurement system, not the values

  // Handle weight change with conversion
  const handleWeightChange = (value: string) => {
    setDisplayWeightValue(value);
    if (value === '' || value === '-' || value === '.') {
      onDataChange?.('weightKg', undefined);
      return;
    }

    if (measurementSystem === 'imperial') {
      const lbs = parseFloat(value);
      if (!isNaN(lbs)) {
        const kg = lbsToKg(lbs);
        onDataChange?.('weightKg', parseFloat(kg.toFixed(2)));
      }
    } else {
      const kg = parseFloat(value);
      if (!isNaN(kg)) {
        onDataChange?.('weightKg', kg);
      }
    }
  };

  // Handle height change for imperial
  const handleHeightFeetChange = (value: string) => {
    setHeightFeet(value);
    updateHeightCm(value, heightInches);
  };

  const handleHeightInchesChange = (value: string) => {
    setHeightInches(value);
    updateHeightCm(heightFeet, value);
  };

  const updateHeightCm = (feet: string, inches: string) => {
    if (feet === '' && inches === '') {
      onDataChange?.('heightCm', undefined);
      return;
    }

    const feetNum = parseInt(feet) || 0;
    const inchesNum = parseInt(inches) || 0;
    const totalInches = feetNum * 12 + inchesNum;
    const cm = totalInches * 2.54;
    onDataChange?.('heightCm', parseFloat(cm.toFixed(0)));
  };

  // Handle waist/hip change for imperial system
  const handleWaistInchesChange = (value: string) => {
    // Allow empty string or partial input (like "3." or "-")
    setWaistInches(value);

    // Only convert and update if we have a complete valid number
    if (value === '' || value === '-' || value === '.') {
      onDataChange?.('waistCircumferenceCm', undefined);
      return;
    }

    const inches = parseFloat(value);
    if (!isNaN(inches) && inches >= 0) {
      // Use common conversion utility
      const cmValue = convertInchesToCm(inches);
      onDataChange?.('waistCircumferenceCm', cmValue);
    } else if (value === '') {
      onDataChange?.('waistCircumferenceCm', undefined);
    }
  };

  const handleHipInchesChange = (value: string) => {
    // Allow empty string or partial input (like "3." or "-")
    setHipInches(value);

    // Only convert and update if we have a complete valid number
    if (value === '' || value === '-' || value === '.') {
      onDataChange?.('hipCircumferenceCm', undefined);
      return;
    }

    const inches = parseFloat(value);
    if (!isNaN(inches) && inches >= 0) {
      // Use common conversion utility
      const cmValue = convertInchesToCm(inches);
      onDataChange?.('hipCircumferenceCm', cmValue);
    } else if (value === '') {
      onDataChange?.('hipCircumferenceCm', undefined);
    }
  };

  // Handle waist/hip change for metric system
  const handleWaistCmChange = (value: string) => {
    // Allow empty string or partial input
    if (value === '' || value === '-' || value === '.') {
      onDataChange?.('waistCircumferenceCm', undefined);
      return;
    }

    const numericValue = parseFloat(value);
    if (!isNaN(numericValue) && numericValue >= 0) {
      onDataChange?.('waistCircumferenceCm', numericValue);
    } else if (value === '') {
      onDataChange?.('waistCircumferenceCm', undefined);
    }
  };

  const handleHipCmChange = (value: string) => {
    // Allow empty string or partial input
    if (value === '' || value === '-' || value === '.') {
      onDataChange?.('hipCircumferenceCm', undefined);
      return;
    }

    const numericValue = parseFloat(value);
    if (!isNaN(numericValue) && numericValue >= 0) {
      onDataChange?.('hipCircumferenceCm', numericValue);
    } else if (value === '') {
      onDataChange?.('hipCircumferenceCm', undefined);
    }
  };

  // Format waist/hip for display
  const formattedWaist = displayWaistCm !== undefined && displayWaistCm !== null
    ? (measurementSystem === 'imperial'
      ? `${cmToInches(Number(displayWaistCm)).toFixed(1)} in`
      : `${Number(displayWaistCm).toFixed(1)} cm`)
    : null;

  const formattedHip = displayHipCm !== undefined && displayHipCm !== null
    ? (measurementSystem === 'imperial'
      ? `${cmToInches(Number(displayHipCm)).toFixed(1)} in`
      : `${Number(displayHipCm).toFixed(1)} cm`)
    : null;

  const formattedIBW = ibw
    ? (measurementSystem === 'imperial'
      ? `${ibw} lbs`
      : `${ibw} kg`)
    : null;

  const getBMIColor = (category: string) => {
    switch (category) {
      case 'Underweight':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Normal weight':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Overweight':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Obese':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <>
      <Card className="bg-white border-cs-primary/20 shadow-sm w-full">
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Basic Measurements Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-6">
              {/* Age */}
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-cs-primary" />
                <div>
                  <p className="text-sm text-gray-500 font-manrope font-medium">Age</p>
                  <p className="font-semibold text-gray-900 font-manrope">
                    {displayAge ? `${displayAge} years` : 'N/A'}
                  </p>
                </div>
              </div>



              {/* Sex at Birth — only when gender is other than male/female */}
              {showSexAtBirthField && (
                <div className="flex items-center gap-3">
                  <TestTube2 className="h-5 w-5 text-cs-primary" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 font-manrope font-medium">Sex at Birth</p>
                    {isEditMode ? (
                      <Select
                        value={editData.sexAtBirth !== undefined ? editData.sexAtBirth : (clientSexAtBirthValue ?? '')}
                        onValueChange={(value) => onDataChange?.('sexAtBirth', value)}
                      >
                        <SelectTrigger className="h-8 text-sm font-manrope">
                          <SelectValue placeholder="Select sex at birth" />
                        </SelectTrigger>
                        <SelectContent>
                          {SEX_AT_BIRTH_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-semibold text-gray-900 capitalize font-manrope">
                        {showSexAtBirthAsNull ? 'null' : (displaySexAtBirth || 'N/A')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Gender */}
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-cs-primary" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-manrope font-medium">Gender</p>
                  {isEditMode ? (
                    <Select
                      value={editData.gender || client.gender || ''}
                      onValueChange={(value) => onDataChange?.('gender', value)}
                    >
                      <SelectTrigger className="h-8 text-sm font-manrope">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-semibold text-gray-900 capitalize font-manrope">
                      {displayGender || 'N/A'}
                    </p>
                  )}
                </div>
              </div>

              {/* Height */}
              <div className="flex items-center gap-3">
                <Ruler className="h-5 w-5 text-cs-secondary" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-manrope font-medium">Height ({measurementSystem === 'imperial' ? 'ft/in' : 'cm'})</p>
                  {isEditMode ? (
                    measurementSystem === 'imperial' ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={heightFeet}
                          onChange={(e) => handleHeightFeetChange(e.target.value)}
                          className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          placeholder="Feet"
                          min={0}
                          max={8}
                        />
                        <span className="text-xs text-gray-500 font-manrope">ft</span>
                        <Input
                          type="number"
                          value={heightInches}
                          onChange={(e) => handleHeightInchesChange(e.target.value)}
                          className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          placeholder="In"
                          min={0}
                          max={11}
                        />
                        <span className="text-xs text-gray-500 font-manrope">in</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={editData.heightCm !== undefined && editData.heightCm !== null ? editData.heightCm : (client.heightCm ?? '')}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || value === '-' || value === '.') {
                              onDataChange?.('heightCm', undefined);
                              return;
                            }
                            const numericValue = Number(value);
                            if (!Number.isFinite(numericValue)) return;
                            onDataChange?.('heightCm', numericValue);
                          }}
                          className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          placeholder="Height"
                          min={100}
                          max={250}
                        />
                        <span className="text-sm text-gray-500 font-manrope">cm</span>
                      </div>
                    )
                  ) : (
                    <p className="font-semibold text-gray-900 font-manrope">
                      {formattedHeight || 'N/A'}
                    </p>
                  )}
                </div>
              </div>

              {/* Weight */}
              <div className="flex items-center gap-3">
                <Weight className="h-5 w-5 text-cs-secondary" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-manrope font-medium">Weight ({measurementSystem === 'imperial' ? 'lbs' : 'kg'})</p>
                  {isEditMode ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={displayWeightValue}
                        onChange={(e) => handleWeightChange(e.target.value)}
                        className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                        placeholder="Weight"
                        min={measurementSystem === 'imperial' ? 50 : 30}
                        max={measurementSystem === 'imperial' ? 660 : 300}
                        step="0.1"
                      />
                      <span className="text-sm text-gray-500 font-manrope">
                        {measurementSystem === 'imperial' ? 'lbs' : 'kg'}
                      </span>
                    </div>
                  ) : (
                    <p className="font-semibold text-gray-900 font-manrope">
                      {formattedWeight || 'N/A'}
                    </p>
                  )}
                </div>
              </div>

              {/* BMI */}
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-cs-primary" />
                <div>
                  <p className="text-sm text-gray-500 font-manrope font-medium">BMI</p>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900 font-manrope">
                      {displayBMI !== undefined && displayBMI !== null ? Number(displayBMI).toFixed(1) : 'N/A'}
                    </p>
                    {displayBMICategory && (
                      <Badge
                        variant="outline"
                        className={`text-xs font-manrope font-medium ${getBMIColor(displayBMICategory)}`}
                      >
                        {displayBMICategory}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Anthropometric Data Section */}
            <div className="border-t pt-6">
              <h3 className="text-base font-semibold text-gray-900 font-manrope mb-4">Anthropometric Data</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Waist Circumference */}
                <div className="flex items-center gap-3">
                  <Ruler className="h-5 w-5 text-cs-secondary" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 font-manrope font-medium">Waist Circumference</p>
                    {isEditMode ? (
                      measurementSystem === 'imperial' ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={waistInches}
                            onChange={(e) => handleWaistInchesChange(e.target.value)}
                            className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                            placeholder="Inches"
                            min={0}
                            step="0.1"
                          />
                          <span className="text-xs text-gray-500 font-manrope">in</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={displayWaistCm !== undefined && displayWaistCm !== null ? displayWaistCm : ''}
                            onChange={(e) => handleWaistCmChange(e.target.value)}
                            className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                            placeholder="cm"
                            min={0}
                            step="0.1"
                          />
                          <span className="text-sm text-gray-500 font-manrope">cm</span>
                        </div>
                      )
                    ) : (
                      <p className="font-semibold text-gray-900 font-manrope">
                        {formattedWaist || 'N/A'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Hip Circumference */}
                <div className="flex items-center gap-3">
                  <Ruler className="h-5 w-5 text-cs-secondary" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 font-manrope font-medium">Hip Circumference</p>
                    {isEditMode ? (
                      measurementSystem === 'imperial' ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={hipInches}
                            onChange={(e) => handleHipInchesChange(e.target.value)}
                            className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                            placeholder="Inches"
                            min={0}
                            step="0.1"
                          />
                          <span className="text-xs text-gray-500 font-manrope">in</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={displayHipCm !== undefined && displayHipCm !== null ? displayHipCm : ''}
                            onChange={(e) => handleHipCmChange(e.target.value)}
                            className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                            placeholder="cm"
                            min={0}
                            step="0.1"
                          />
                          <span className="text-sm text-gray-500 font-manrope">cm</span>
                        </div>
                      )
                    ) : (
                      <p className="font-semibold text-gray-900 font-manrope">
                        {formattedHip || 'N/A'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Body Fat Percentage */}
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-cs-primary" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 font-manrope font-medium">Body Fat Percentage</p>
                    {isEditMode ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={displayBodyFat !== undefined && displayBodyFat !== null ? displayBodyFat : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string or partial input
                            if (value === '' || value === '-' || value === '.') {
                              onDataChange?.('bodyFatPercentage', undefined);
                              return;
                            }
                            const numericValue = parseFloat(value);
                            if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                              onDataChange?.('bodyFatPercentage', numericValue);
                            }
                          }}
                          className="h-8 text-sm font-manrope flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          placeholder="%"
                          min={0}
                          max={100}
                          step="0.1"
                        />
                        <span className="text-sm text-gray-500 font-manrope">%</span>
                      </div>
                    ) : (
                      <p className="font-semibold text-gray-900 font-manrope">
                        {displayBodyFat !== undefined && displayBodyFat !== null ? `${Number(displayBodyFat).toFixed(1)}%` : 'N/A'}
                      </p>
                    )}
                  </div>
                </div>

                {/* WHR (Read-only calculated) */}
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-cs-primary" />
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-sm text-gray-500 font-manrope font-medium">WHR</p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Waist-to-Hip Ratio = Waist ÷ Hip</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="font-semibold text-gray-900 font-manrope">
                      {whr !== null ? Number(whr).toFixed(2) : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* IBW (Read-only calculated) */}
                <div className="flex items-center gap-3">
                  <Weight className="h-5 w-5 text-cs-secondary" />
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-sm text-gray-500 font-manrope font-medium">IBW</p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">
                              {measurementSystem === 'imperial'
                                ? `Ideal Body Weight (Devine Formula): ${isMale ? 'Men: 110 + (5 × inches over 5 ft)' : 'Women: 100 + (5 × inches over 5 ft)'}`
                                : `Ideal Body Weight (Devine Formula): ${isMale ? 'Men: 50 + 2.3 × (height in inches - 60)' : 'Women: 45.5 + 2.3 × (height in inches - 60)'}`}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="font-semibold text-gray-900 font-manrope">
                      {formattedIBW || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Calculation Formula */}
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-cs-primary" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 font-manrope font-medium">Calculation Formula</p>
                    {isEditMode ? (
                      <Select
                        value={formulaSelectValue}
                        onValueChange={handleFormulaChange}
                        disabled={isLoadingFormulas || allFormulas.length === 0}
                      >
                        <SelectTrigger className="h-8 text-sm font-manrope">
                          <SelectValue placeholder={isLoadingFormulas ? "Loading..." : "Select formula"}>
                            {formulaSelectValue
                              ? (allFormulas.find(f => String(f.id) === formulaSelectValue)?.displayName ||
                                allFormulas.find(f => String(f.id) === formulaSelectValue)?.formulaName ||
                                undefined)
                              : undefined}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {allFormulas.map((formula) => (
                            <SelectItem key={formula.id} value={String(formula.id)}>
                              {formula.displayName || formula.formulaName || 'Unknown Formula'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-semibold text-gray-900 font-manrope text-xs truncate">
                        {formulaName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity & Goals Section */}
            <div className="border-t pt-6">
              <h3 className="text-base font-semibold text-gray-900 font-manrope mb-4">Activity and Goals</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                {/* Physical Activity Level - Hidden for infants (0-12 months) */}
                {!isInfant && (
                  <div className="flex items-center gap-3">
                    <Activity className="h-5 w-5 text-cs-primary" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 font-manrope font-medium">Physical Activity Level</p>
                      {isEditMode ? (
                        <Select
                          value={displayActivityLevel || ''}
                          onValueChange={(value) => onDataChange?.('activityLevel', value)}
                          disabled={!allowedActivityLevels || allowedActivityLevels.options.length === 0}
                        >
                          <SelectTrigger className="h-8 text-sm font-manrope">
                            <SelectValue placeholder={allowedActivityLevels ? "Select activity level" : "Loading..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {allowedActivityLevels?.options.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label} - {option.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-semibold text-gray-900 font-manrope">
                          {displayActivityLevel
                            ? (allowedActivityLevels?.options.find(o => o.value === displayActivityLevel)?.label || displayActivityLevel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                            : 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Feeding Type - Only show for infants (0-12 months) */}
                {isInfant && (
                  <div className="flex items-center gap-3">
                    <Baby className="h-5 w-5 text-cs-primary" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 font-manrope font-medium">Feeding Type</p>
                      {isEditMode ? (
                        <Select
                          value={displayFeedingType || ''}
                          onValueChange={(value) => onDataChange?.('feedingType', value)}
                        >
                          <SelectTrigger className="h-8 text-sm font-manrope">
                            <SelectValue placeholder="Select feeding type" />
                          </SelectTrigger>
                          <SelectContent>
                            {FEEDING_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-semibold text-gray-900 font-manrope">
                          {displayFeedingType
                            ? FEEDING_TYPE_OPTIONS.find(o => o.value === displayFeedingType)?.label || displayFeedingType
                            : 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Weight Goal */}
                {(!isPregnant || isLactating) && (displayAge === undefined || displayAge >= 18) && (
                  <div className="flex items-center gap-3">
                    <Target className="h-5 w-5 text-cs-primary" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 font-manrope font-medium">Weight Loss Goal</p>
                      {isEditMode ? (
                        <Select
                          value={displayWeightGoal || ''}
                          onValueChange={(value) => onDataChange?.('weightLossGoal', value)}
                        >
                          <SelectTrigger className="h-8 text-sm font-manrope">
                            <SelectValue placeholder="Select weight goal" />
                          </SelectTrigger>
                          <SelectContent>
                            {(isLactating ? WEIGHT_LOSS_GOAL_OPTIONS : GENERAL_WEIGHT_GOAL_OPTIONS).map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={isPregnant && option.value === 'weight_loss'}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-semibold text-gray-900 font-manrope">
                          {displayWeightGoal
                            ? (isLactating ? WEIGHT_LOSS_GOAL_OPTIONS : GENERAL_WEIGHT_GOAL_OPTIONS).find(o => o.value === displayWeightGoal)?.label || displayWeightGoal
                            : 'N/A'}
                          {isPregnant && displayWeightGoal === 'weight_loss' && (
                            <span className="ml-2 text-xs text-amber-600 font-medium">(Not recommended during pregnancy)</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* BMR */}
                {shouldShowBmr && (
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-cs-secondary" />
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <p className="text-sm text-gray-500 font-manrope font-medium">BMR</p>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-3 w-3 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Basal Metabolic Rate</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className={`font-semibold font-manrope ${(isPregnant || isLactating) ? 'text-gray-400' : 'text-gray-900'}`}>
                        {displayBmr != null && !isNaN(Number(displayBmr)) ? `${Math.round(Number(displayBmr))} kcal/day` : 'N/A'}
                      </p>
                    </div>
                  </div>
                )}

                {/* EER */}
                <div className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-cs-primary" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1">
                      <p className="text-sm text-gray-500 font-manrope font-medium">EER</p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Estimated Energy Requirement</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="font-semibold text-gray-900 font-manrope">
                      {displayEer != null && !isNaN(Number(displayEer)) ? `${Math.round(Number(displayEer))} kcal/day` : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Pregnancy (Female only, 18+) */}
                {biologicalSexForCalculations === 'female' && !isFemaleUnder18 && (
                  <>
                    {/* Pregnancy Status */}
                    <div className="flex items-center gap-3">
                      <Baby className="h-5 w-5 text-cs-secondary" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 font-manrope font-medium">Pregnancy Status</p>
                        {isEditMode ? (
                          <Select
                            value={isPregnant ? 'pregnant' : 'not_pregnant'}
                            onValueChange={(value) => {
                              const newIsPregnant = value === 'pregnant';

                              if (newIsPregnant) {
                                // Default to first trimester if becoming pregnant
                                onDataChange?.('pregnancyTrimester', 'first');

                                // Clear the general weight goal only when it is not needed for lactation.
                                if (!isLactating && displayWeightGoal === 'weight_loss') {
                                  onDataChange?.('weightLossGoal', null);
                                }
                              } else {
                                // Clear pregnancy-related fields
                                onDataChange?.('pregnancyTrimester', 'none');
                                if (!isLactating) {
                                  onDataChange?.('isTwinPregnancy', false);
                                }
                              }
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm font-manrope">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_pregnant">Not Pregnant</SelectItem>
                              <SelectItem value="pregnant">Pregnant</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="font-semibold text-gray-900 font-manrope">
                            {isPregnant ? 'Pregnant' : 'Not Pregnant'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Trimester (only shown if pregnant) */}
                    {isPregnant && (
                      <div className="flex items-center gap-3">
                        <Baby className="h-5 w-5 text-cs-secondary" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-500 font-manrope font-medium">Pregnancy Trimester</p>
                          {isEditMode ? (
                            <Select
                              value={displayPregnancyTrimester || ''}
                              onValueChange={(value) => onDataChange?.('pregnancyTrimester', value)}
                            >
                              <SelectTrigger className="h-8 text-sm font-manrope">
                                <SelectValue placeholder="Select trimester" />
                              </SelectTrigger>
                              <SelectContent>
                                {PREGNANCY_TRIMESTER_OPTIONS.filter(o => o.value !== 'none').map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="font-semibold text-gray-900 font-manrope">
                              {displayPregnancyTrimester
                                ? PREGNANCY_TRIMESTER_OPTIONS.find(o => o.value === displayPregnancyTrimester)?.label || displayPregnancyTrimester
                                : 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pregnancy Type (only shown if pregnant) */}
                    {isPregnant && (
                      <div className="flex items-center gap-3">
                        <Baby className="h-5 w-5 text-cs-secondary" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-500 font-manrope font-medium">Pregnancy Type</p>
                          {isEditMode ? (
                            <Select
                              value={displayIsTwinPregnancy ? 'twin' : 'singleton'}
                              onValueChange={(value) => onDataChange?.('isTwinPregnancy', value === 'twin')}
                            >
                              <SelectTrigger className="h-8 text-sm font-manrope">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="singleton">Singleton</SelectItem>
                                <SelectItem value="twin">Twin Pregnancy</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="font-semibold text-gray-900 font-manrope">
                              {displayIsTwinPregnancy ? 'Twin Pregnancy' : 'Singleton'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Lactation (Female only, 18+) */}
                {biologicalSexForCalculations === 'female' && !isFemaleUnder18 && (
                  <>
                    <div className="flex items-center gap-3">
                      <Baby className="h-5 w-5 text-cs-primary" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 font-manrope font-medium">Lactation Status</p>
                        {isEditMode ? (
                          <Select
                            value={displayLactationStatus || 'none'}
                            onValueChange={(value) => onDataChange?.('lactationPeriod', value)}
                          >
                            <SelectTrigger className="h-8 text-sm font-manrope">
                              <SelectValue placeholder="Select lactation status" />
                            </SelectTrigger>
                            <SelectContent>
                              {LACTATION_STATUS_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="font-semibold text-gray-900 font-manrope">
                            {displayLactationStatus
                              ? LACTATION_STATUS_OPTIONS.find(o => o.value === displayLactationStatus)?.label || displayLactationStatus
                              : 'Not Lactating'}
                          </p>
                        )}
                      </div>
                    </div>

                    {(displayLactationStatus === '12-18' || displayLactationStatus === '18-24') && (
                      <Alert className="border-amber-500/60 bg-amber-50 text-amber-900 dark:border-amber-400/60 dark:bg-amber-900/30 dark:text-amber-50">
                        <AlertDescription className="text-sm">
                          Extended lactation (&gt;12mo): Nutrient values based on 6-12mo recommendations.
                          Breast milk production typically decreases as complementary foods increase.
                          Consult healthcare provider for personalized guidance.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Pregnancy Type (only shown if lactating) */}
                    {displayLactationStatus && displayLactationStatus !== 'none' && !isPregnant && (
                      <div className="flex items-center gap-3">
                        <Baby className="h-5 w-5 text-cs-secondary" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-500 font-manrope font-medium">Pregnancy Type</p>
                          {isEditMode ? (
                            <Select
                              value={displayIsTwinPregnancy ? 'twin' : 'singleton'}
                              onValueChange={(value) => onDataChange?.('isTwinPregnancy', value === 'twin')}
                            >
                              <SelectTrigger className="h-8 text-sm font-manrope">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="singleton">Singleton</SelectItem>
                                <SelectItem value="twin">Twin Pregnancy</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="font-semibold text-gray-900 font-manrope">
                              {displayIsTwinPregnancy ? 'Twin Pregnancy' : 'Singleton'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>


            {/* Symptoms Section */}
            <div className="border-t pt-6">
              <h3 className="text-base font-semibold text-gray-900 font-manrope mb-4">Symptoms</h3>
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-cs-primary" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500 font-manrope font-medium">Reported Symptoms</p>
                  <p className="font-semibold text-gray-900 font-manrope">
                    {getSymptomsDisplay(client.symptoms)}
                  </p>
                </div>
              </div>
            </div>

            {/* Biomarkers Section */}
            {client.biomarkers && client.biomarkers.length > 0 && (
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900 font-manrope">Biomarkers</h3>
                  <span className="text-xs text-muted-foreground">{client.biomarkers.length} results</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {client.biomarkers.map((biomarker) => (
                    <div key={biomarker.id} className="flex items-center justify-between p-2.5 rounded-md border border-border/40 bg-card hover:bg-muted/20 transition-colors group">
                      <div className="min-w-0 flex-1 mr-3">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="font-medium text-sm text-foreground truncate" title={biomarker.biomarkerName || biomarker.biomarkerId}>
                            {biomarker.biomarkerName || biomarker.biomarkerId.replace(/_/g, ' ')}
                          </span>
                          {biomarker.interpretation && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-3 w-3 text-muted-foreground/70 hover:text-primary transition-colors" />
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p className="text-xs max-w-xs">{biomarker.interpretation}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {biomarker.testedDate ? new Date(biomarker.testedDate).toLocaleDateString() : 'No date'}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="flex items-baseline gap-1">
                          <span className="font-bold text-sm text-gray-900">{biomarker.value}</span>
                          {biomarker.unit && <span className="text-xs font-normal text-muted-foreground">{biomarker.unit}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Formula validation warning modal */}
      <FormulaValidationModal
        open={showFormulaValidationModal}
        onOpenChange={setShowFormulaValidationModal}
        selectedFormulaName={nonViableFormulaName}
        fallbackFormulaName={defaultFormulaDisplayName}
        onConfirm={handleFormulaValidationConfirm}
      />
    </>
  );
};

export default ClientProfileSummary;
